import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";



import NewHomePage from "@/pages/NewHomePage";

// Blog

// Learning Hub
import { LearningAuthProvider, useLearningAuth } from "@/context/LearningAuthContext";

// CRM Module
import { CrmAuthProvider, useCrmAuth } from "@/context/CrmAuthContext";
import { CrmThemeProvider } from "@/context/CrmThemeContext";


// Bright EOR Employee PWA (separate identity from CRM)
import { EorPortalAuthProvider, useEorPortalAuth } from "@/context/EorPortalAuthContext";


// Brightridge Partners Intern PWA (separate portal, orange accent)
import { PartnersPortalAuthProvider, usePartnersPortalAuth } from "@/context/PartnersPortalAuthContext";
const Header = lazy(() => import("@/components/layout/Header").then((m) => ({ default: m.Header })));
const Footer = lazy(() => import("@/components/layout/Footer").then((m) => ({ default: m.Footer })));
const EventBanner = lazy(() => import("@/components/layout/EventBanner").then((m) => ({ default: m.EventBanner })));
const Toaster = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
const CrmLayout = lazy(() => import("@/components/CrmLayout"));
const BrightLabsCmsPage = lazy(() => import("@/lib/BrightLabsCMS").then((m) => ({ default: m.BrightLabsCmsPage })));

const HomePage = lazy(() => import("@/pages/HomePage"));
const HirePage = lazy(() => import("@/pages/HirePage"));
const BuildPage = lazy(() => import("@/pages/BuildPage"));
const DevelopPage = lazy(() => import("@/pages/DevelopPage"));
const ScalePage = lazy(() => import("@/pages/ScalePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const WebinarRegistrationPage = lazy(() => import("@/pages/WebinarRegistrationPage"));
const WebinarPresentationPage = lazy(() => import("@/pages/WebinarPresentationPage"));
const WebinarAdminPage = lazy(() => import("@/pages/WebinarAdminPage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const ChildrensHomesCareersPage = lazy(() => import("@/pages/ChildrensHomesCareersPage"));
const IntelligenceDashboardPage = lazy(() => import("@/pages/IntelligenceDashboardPage"));
const HealthcareDashboardPage = lazy(() => import("@/pages/HealthcareDashboardPage"));
const CallPrepPage = lazy(() => import("@/pages/CallPrepPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const BlogListPage = lazy(() => import("@/pages/blog/BlogListPage"));
const BlogPostPage = lazy(() => import("@/pages/blog/BlogPostPage"));
const LearningLandingPage = lazy(() => import("@/pages/learning/LearningLandingPage"));
const LearningAuthPage = lazy(() => import("@/pages/learning/LearningAuthPage"));
const LearningPathwayPage = lazy(() => import("@/pages/learning/LearningPathwayPage"));
const LearningDashboardPage = lazy(() => import("@/pages/learning/LearningDashboardPage"));
const InterviewCoachPage = lazy(() => import("@/pages/learning/InterviewCoachPage"));
const LearningCategoryPage = lazy(() => import("@/pages/learning/LearningCategoryPage"));
const LearningModulePage = lazy(() => import("@/pages/learning/LearningModulePage"));
const LearningResourcesPage = lazy(() => import("@/pages/learning/LearningResourcesPage"));
const AssessmentHubPage = lazy(() => import("@/pages/learning/AssessmentHubPage"));
const AssessmentQuizPage = lazy(() => import("@/pages/learning/AssessmentQuizPage"));
const AssessmentResultsPage = lazy(() => import("@/pages/learning/AssessmentResultsPage"));
const RefresherListPage = lazy(() => import("@/pages/learning/RefresherListPage"));
const RefresherModulePage = lazy(() => import("@/pages/learning/RefresherModulePage"));
const ResetPasswordPage = lazy(() => import("@/pages/learning/ResetPasswordPage"));
const CrmLoginPage = lazy(() => import("@/pages/crm/CrmLoginPage"));
const CrmDashboardPage = lazy(() => import("@/pages/crm/CrmDashboardPage"));
const CrmCandidatesPage = lazy(() => import("@/pages/crm/CrmCandidatesPage"));
const CrmCandidateProfilePage = lazy(() => import("@/pages/crm/CrmCandidateProfilePage"));
const CrmClientsPage = lazy(() => import("@/pages/crm/CrmClientsPage"));
const CrmClientProfilePage = lazy(() => import("@/pages/crm/CrmClientProfilePage"));
const CrmJobsPage = lazy(() => import("@/pages/crm/CrmJobsPage"));
const CrmJobProfilePage = lazy(() => import("@/pages/crm/CrmJobProfilePage"));
const CrmPipelinePage = lazy(() => import("@/pages/crm/CrmPipelinePage"));
const CrmCallbacksPage = lazy(() => import("@/pages/crm/CrmCallbacksPage"));
const CrmFavoritesPage = lazy(() => import("@/pages/crm/CrmFavoritesPage"));
const CrmReportsPage = lazy(() => import("@/pages/crm/CrmReportsPage"));
const CrmUserManagementPage = lazy(() => import("@/pages/crm/CrmUserManagementPage"));
const CrmWorkstreamsPage = lazy(() => import("@/pages/crm/CrmWorkstreamsPage"));
const CrmWorkstreamDetailPage = lazy(() => import("@/pages/crm/CrmWorkstreamDetailPage"));
const CrmEorDashboardPage = lazy(() => import("@/pages/crm/CrmEorDashboardPage"));
const CrmEorClientsPage = lazy(() => import("@/pages/crm/CrmEorClientsPage"));
const CrmEorCandidatesPage = lazy(() => import("@/pages/crm/CrmEorCandidatesPage"));
const CrmEorCandidateProfilePage = lazy(() => import("@/pages/crm/CrmEorCandidateProfilePage"));
const CrmCompanySettingsPage = lazy(() => import("@/pages/crm/CrmCompanySettingsPage"));
const CrmInvoicesPage = lazy(() => import("@/pages/crm/CrmInvoicesPage"));
const CrmInvoiceEditorPage = lazy(() => import("@/pages/crm/CrmInvoiceEditorPage"));
const CrmInvoiceDetailPage = lazy(() => import("@/pages/crm/CrmInvoiceDetailPage"));
const CrmContractsPage = lazy(() => import("@/pages/crm/CrmContractsPage"));
const CrmContractNewPage = lazy(() => import("@/pages/crm/CrmContractNewPage"));
const CrmContractDetailPage = lazy(() => import("@/pages/crm/CrmContractDetailPage"));
const CrmJobApplicationsPage = lazy(() => import("@/pages/crm/CrmJobApplicationsPage"));
const PublicJobApplicationPage = lazy(() => import("@/pages/PublicJobApplicationPage"));
const PublicInvoicePage = lazy(() => import("@/pages/PublicInvoicePage"));
const CrmEorEmployeesPage = lazy(() => import("@/pages/crm/CrmEorEmployeesPage"));
const CrmZohoSettingsPage = lazy(() => import("@/pages/crm/CrmZohoSettingsPage"));
const CrmBlogPage = lazy(() => import("@/pages/crm/CrmBlogPage"));
const CrmLearnersPage = lazy(() => import("@/pages/crm/CrmLearnersPage"));
const CrmROIPage = lazy(() => import("@/pages/crm/CrmROIPage"));
const CrmPlannerPage = lazy(() => import("@/pages/crm/CrmPlannerPage"));
const CrmPlannerPopout = lazy(() => import("@/pages/crm/CrmPlannerPopout"));
const CrmSignaturesPage = lazy(() => import("@/pages/crm/CrmSignaturesPage"));
const PublicSignaturePage = lazy(() => import("@/pages/PublicSignaturePage"));
const CrmPortalEmployeesPage = lazy(() => import("@/pages/crm/CrmPortalEmployeesPage"));
const CrmPortalLeavePage = lazy(() => import("@/pages/crm/CrmPortalLeavePage"));
const CrmPortalAttendancePage = lazy(() => import("@/pages/crm/CrmPortalAttendancePage"));
const EorPortalLayout = lazy(() => import("@/pages/eor-portal/EorPortalLayout"));
const PortalLandingPage = lazy(() => import("@/pages/eor-portal/PortalLandingPage"));
const PortalLoginPage = lazy(() => import("@/pages/eor-portal/PortalLoginPage"));
const PortalAcceptInvitePage = lazy(() => import("@/pages/eor-portal/PortalAcceptInvitePage"));
const PortalHomePage = lazy(() => import("@/pages/eor-portal/PortalHomePage"));
const PortalDocumentsPage = lazy(() => import("@/pages/eor-portal/PortalDocumentsPage"));
const PortalPoliciesPage = lazy(() => import("@/pages/eor-portal/PortalPoliciesPage"));
const PortalArchivePage = lazy(() => import("@/pages/eor-portal/PortalArchivePage"));
const PortalProfilePage = lazy(() => import("@/pages/eor-portal/PortalProfilePage"));
const PortalAttendancePage = lazy(() => import("@/pages/eor-portal/PortalAttendancePage"));
const PortalLeavePage = lazy(() => import("@/pages/eor-portal/PortalLeavePage"));
const PortalEquipmentPage = lazy(() => import("@/pages/eor-portal/PortalEquipmentPage"));
const PortalMorePage = lazy(() => import("@/pages/eor-portal/PortalMorePage"));
const PartnersPortalLayout = lazy(() => import("@/pages/partners-portal/PartnersPortalLayout"));
const PartnersLoginPage = lazy(() => import("@/pages/partners-portal/PartnersLoginPage"));
const PartnersAcceptInvitePage = lazy(() => import("@/pages/partners-portal/PartnersAcceptInvitePage"));
const PartnersHomePage = lazy(() => import("@/pages/partners-portal/PartnersHomePage"));
const PartnersContractPage = lazy(() => import("@/pages/partners-portal/PartnersContractPage"));
const PartnersOnboardingPage = lazy(() => import("@/pages/partners-portal/PartnersOnboardingPage"));
const PartnersProfilePage = lazy(() => import("@/pages/partners-portal/PartnersProfilePage"));
const PartnersMorePage = lazy(() => import("@/pages/partners-portal/PartnersMorePage"));
const CrmPartnersInternsPage = lazy(() => import("@/pages/crm/CrmPartnersInternsPage"));

const CrmProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useCrmAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div></div>;
  if (!isAuthenticated) return <Navigate to="/crm/login" replace />;
  return children;
};

const EorPortalProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useEorPortalAuth();
  if (loading) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-600"></div></div>;
  if (!isAuthenticated) return <Navigate to="/portal/login" replace />;
  return children;
};

const PartnersPortalProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = usePartnersPortalAuth();
  if (loading) return <div className="min-h-screen bg-[#FAF7F3] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#E8803A]"></div></div>;
  if (!isAuthenticated) return <Navigate to="/partners/login" replace />;
  return children;
};

const LearningProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useLearningAuth();
  if (loading) return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#E8803A]/30 border-t-[#E8803A] rounded-full animate-spin"></div></div>;
  if (!isAuthenticated) return <Navigate to="/pathways/login" replace />;
  return children;
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

const DeferredToaster = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);
  return ready ? <Suspense fallback={null}><Toaster position="top-right" /></Suspense> : null;
};

function App() {
  return (
    <HelmetProvider>
    <CrmAuthProvider>
    <EorPortalAuthProvider>
    <PartnersPortalAuthProvider>
    <LearningAuthProvider>
    <BrowserRouter>
    <CrmThemeProvider>
      <ScrollToTop />
      <Suspense fallback={<div className="min-h-screen" />}>
      <Routes>
        {/* Learning Hub */}
        <Route path="/pathways" element={<LearningLandingPage />} />
        <Route path="/pathways/interview-coach" element={<InterviewCoachPage />} />
        <Route path="/pathways/login" element={<LearningAuthPage />} />
        <Route path="/pathways/register" element={<LearningAuthPage />} />
        <Route path="/pathways/forgot-password" element={<ResetPasswordPage />} />
        <Route path="/pathways/reset-password" element={<ResetPasswordPage />} />
        <Route path="/pathways/pathway" element={<LearningProtectedRoute><LearningPathwayPage /></LearningProtectedRoute>} />
        <Route path="/pathways/dashboard" element={<LearningProtectedRoute><LearningDashboardPage /></LearningProtectedRoute>} />
        <Route path="/pathways/resources" element={<LearningProtectedRoute><LearningResourcesPage /></LearningProtectedRoute>} />
        <Route path="/pathways/categories/:categoryId" element={<LearningProtectedRoute><LearningCategoryPage /></LearningProtectedRoute>} />
        <Route path="/pathways/modules/:moduleId" element={<LearningProtectedRoute><LearningModulePage /></LearningProtectedRoute>} />
        <Route path="/pathways/assessments" element={<LearningProtectedRoute><AssessmentHubPage /></LearningProtectedRoute>} />
        <Route path="/pathways/assessments/:type" element={<LearningProtectedRoute><AssessmentQuizPage /></LearningProtectedRoute>} />
        <Route path="/pathways/assessments/:type/results" element={<LearningProtectedRoute><AssessmentResultsPage /></LearningProtectedRoute>} />
        <Route path="/pathways/assessments/:type/refreshers" element={<LearningProtectedRoute><RefresherListPage /></LearningProtectedRoute>} />
        <Route path="/pathways/assessments/:type/refreshers/:category" element={<LearningProtectedRoute><RefresherModulePage /></LearningProtectedRoute>} />
        {/* Redirects from old RM Readiness URLs */}
        <Route path="/rm-readiness" element={<Navigate to="/pathways" replace />} />
        <Route path="/rm-readiness/*" element={<Navigate to="/pathways" replace />} />

        {/* Standalone pages without main header/footer */}
        <Route path="/webinar/:slug/live" element={<WebinarPresentationPage />} />
        <Route path="/admin/webinars" element={<WebinarAdminPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/intelligence" element={<IntelligenceDashboardPage />} />
        <Route path="/admin/healthcare" element={<HealthcareDashboardPage />} />
        <Route path="/admin/call-prep" element={<CallPrepPage />} />
        <Route path="/childrens-homes" element={<Navigate to="/" replace />} />
        <Route path="/childrens-homes-recruitment" element={<Navigate to="/" replace />} />
        <Route path="/childrens-homes-careers" element={<Navigate to="/" replace />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

        {/* CRM Module */}
        <Route path="/crm/login" element={<CrmLoginPage />} />
        <Route path="/crm/planner/popout" element={<CrmProtectedRoute><CrmPlannerPopout /></CrmProtectedRoute>} />
        <Route path="/crm" element={<CrmProtectedRoute><CrmLayout /></CrmProtectedRoute>}>
          <Route index element={<CrmDashboardPage />} />
          <Route path="planner" element={<CrmPlannerPage />} />
          <Route path="candidates" element={<CrmCandidatesPage />} />
          <Route path="candidates/:id" element={<CrmCandidateProfilePage />} />
          <Route path="clients" element={<CrmClientsPage />} />
          <Route path="clients/:id" element={<CrmClientProfilePage />} />
          <Route path="jobs" element={<CrmJobsPage defaultServiceLine="recruitment" />} />
          <Route path="jobs/:id" element={<CrmJobProfilePage />} />
          <Route path="pipeline" element={<CrmPipelinePage />} />
          <Route path="callbacks" element={<CrmCallbacksPage />} />
          <Route path="favorites" element={<CrmFavoritesPage />} />
          <Route path="reports" element={<CrmReportsPage />} />
          <Route path="users" element={<CrmUserManagementPage />} />
          <Route path="workstreams" element={<CrmWorkstreamsPage />} />
          <Route path="workstreams/:id" element={<CrmWorkstreamDetailPage />} />
          <Route path="eor" element={<CrmEorDashboardPage />} />
          <Route path="eor/candidates" element={<CrmEorCandidatesPage />} />
          <Route path="eor/candidates/:id" element={<CrmEorCandidateProfilePage />} />
          <Route path="settings/company" element={<CrmCompanySettingsPage />} />
          <Route path="eor/jobs" element={<CrmJobsPage defaultServiceLine="eor" isEorPage={true} />} />
          <Route path="eor/jobs/:id" element={<CrmJobProfilePage />} />
          <Route path="eor/jobs/:id/applications" element={<CrmJobApplicationsPage />} />
          <Route path="eor/clients/:id" element={<CrmEorClientsPage />} />
          <Route path="eor/employees/:id" element={<CrmEorEmployeesPage />} />
          <Route path="settings/email" element={<CrmZohoSettingsPage />} />
          <Route path="blog" element={<CrmBlogPage />} />
          <Route path="learners" element={<CrmLearnersPage />} />
          <Route path="roi" element={<CrmROIPage />} />
          <Route path="intelligence" element={<IntelligenceDashboardPage />} />
          <Route path="webinars" element={<WebinarAdminPage />} />
          <Route path="call-prep" element={<CallPrepPage />} />
          <Route path="healthcare" element={<HealthcareDashboardPage />} />
          <Route path="signatures" element={<CrmSignaturesPage />} />
          <Route path="invoices" element={<CrmInvoicesPage />} />
          <Route path="invoices/new" element={<CrmInvoiceEditorPage />} />
          <Route path="invoices/:id" element={<CrmInvoiceDetailPage />} />
          <Route path="invoices/:id/edit" element={<CrmInvoiceEditorPage />} />
          <Route path="contracts" element={<CrmContractsPage />} />
          <Route path="contracts/new" element={<CrmContractNewPage />} />
          <Route path="contracts/:id" element={<CrmContractDetailPage />} />
          <Route path="jobs/:id/applications" element={<CrmJobApplicationsPage />} />
          <Route path="eor/portal-employees" element={<CrmPortalEmployeesPage />} />
          <Route path="eor/portal-leave" element={<CrmPortalLeavePage />} />
          <Route path="eor/portal-attendance" element={<CrmPortalAttendancePage />} />
          <Route path="partners/interns" element={<CrmPartnersInternsPage />} />
        </Route>

        {/* Bright EOR Employee PWA — separate app under /portal */}
        <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/portal/accept-invite" element={<PortalAcceptInvitePage />} />
        <Route path="/portal" element={<EorPortalProtectedRoute><EorPortalLayout /></EorPortalProtectedRoute>}>
          <Route index element={<PortalHomePage />} />
          <Route path="attendance" element={<PortalAttendancePage />} />
          <Route path="leave" element={<PortalLeavePage />} />
          <Route path="documents" element={<PortalDocumentsPage />} />
          <Route path="policies" element={<PortalPoliciesPage />} />
          <Route path="archive" element={<PortalArchivePage />} />
          <Route path="equipment" element={<PortalEquipmentPage />} />
          <Route path="profile" element={<PortalProfilePage />} />
          <Route path="more" element={<PortalMorePage />} />
        </Route>
        <Route path="/portal-welcome" element={<PortalLandingPage />} />

        {/* Brightridge Partners Intern PWA — separate app under /partners */}
        <Route path="/partners/login" element={<PartnersLoginPage />} />
        <Route path="/partners/accept-invite" element={<PartnersAcceptInvitePage />} />
        <Route path="/partners" element={<PartnersPortalProtectedRoute><PartnersPortalLayout /></PartnersPortalProtectedRoute>}>
          <Route index element={<PartnersHomePage />} />
          <Route path="contract" element={<PartnersContractPage />} />
          <Route path="onboarding" element={<PartnersOnboardingPage />} />
          <Route path="profile" element={<PartnersProfilePage />} />
          <Route path="more" element={<PartnersMorePage />} />
        </Route>

        {/* BrightLabs Growth Engine — SCOPED SEO/marketing pages under /services/*
            Deliberately NOT a catch-all: cannot shadow /crm, /portal, /api or any other route. */}
        <Route path="/services/*" element={<BrightLabsCmsPage basePath="/services" />} />
        
        {/* Public e-signature page (no auth) */}
        <Route path="/sign/:token" element={<PublicSignaturePage />} />

        {/* Public invoice viewer (no auth) — for emailed client links */}
        <Route path="/invoice/:token" element={<PublicInvoicePage />} />

        {/* Public job application portal — /apply/:slug */}
        <Route path="/apply/:slug" element={<PublicJobApplicationPage />} />
        
        {/* New homepage */}
        <Route path="/" element={<NewHomePage />} />

        {/* Legacy pages (archived, accessible but not in main nav) */}
        <Route path="/legacy/*" element={
          <>
            <EventBanner />
            <Header />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/hire" element={<HirePage />} />
              <Route path="/build" element={<BuildPage />} />
              <Route path="/develop" element={<DevelopPage />} />
              <Route path="/scale" element={<ScalePage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/webinar/:slug" element={<WebinarRegistrationPage />} />
            </Routes>
            <Footer />
          </>
        } />

        {/* Redirect old top-level routes to legacy */}
        <Route path="/hire" element={<Navigate to="/legacy/hire" replace />} />
        <Route path="/build" element={<Navigate to="/legacy/build" replace />} />
        <Route path="/develop" element={<Navigate to="/legacy/develop" replace />} />
        <Route path="/scale" element={<Navigate to="/legacy/scale" replace />} />
        <Route path="/events" element={<Navigate to="/legacy/events" replace />} />
        <Route path="/webinar/:slug" element={<Navigate to="/legacy/webinar/:slug" replace />} />
      </Routes>
      </Suspense>
      <DeferredToaster />
    </CrmThemeProvider>
    </BrowserRouter>
    </LearningAuthProvider>
    </PartnersPortalAuthProvider>
    </EorPortalAuthProvider>
    </CrmAuthProvider>
    </HelmetProvider>
  );
}

export default App;
